import type { BibleLocation } from '@skjv/common';
import { getChapterId, getBookInfo, parseChapterId } from '@skjv/common';

export interface ChapterLoaderState {
  container: HTMLElement;
  htmlBasePath: string;
  loadedChapters: Map<string, HTMLElement>;
  loadingChapters: Set<string>;
  initVersion: number;
  onChapterLoaded: (chapterId: string, element: HTMLElement) => void;
  addBookmarkIndicators: (chapterElement: HTMLElement, location: BibleLocation) => void;
  applyStudyPassageToChapter: (chapterElement: HTMLElement) => void;
}

/** Load a chapter's HTML and insert it into the container */
export async function loadChapter(
  state: ChapterLoaderState,
  location: BibleLocation,
  position: 'before' | 'after',
  signal?: AbortSignal,
): Promise<boolean> {
  const chapterId = getChapterId(location);
  const capturedVersion = state.initVersion;

  // Prevent duplicate loading
  if (state.loadedChapters.has(chapterId) || state.loadingChapters.has(chapterId)) {
    return false;
  }

  const bookInfo = getBookInfo(location.testamentIndex, location.bookIndex);
  if (!bookInfo) return false;

  // Mark as loading BEFORE any async work
  state.loadingChapters.add(chapterId);

  try {
    // Both chapterIndex and file names are 0-based. The build stamp busts
    // browser HTTP caches — chapter filenames never change across deploys.
    const url = `${state.htmlBasePath}/${bookInfo.fileName}/${location.chapterIndex}.html?v=${__BUILD_ID__}`;
    const response = await fetch(url, { signal });

    // Check if we've been invalidated during fetch
    if (state.initVersion !== capturedVersion) {
      return false;
    }

    if (!response.ok) {
      console.error(`Failed to load chapter: ${url}`);
      return false;
    }

    const html = await response.text();

    // Check again after reading response
    if (state.initVersion !== capturedVersion) {
      return false;
    }

    const wrapper = document.createElement('div');
    wrapper.innerHTML = html;
    const chapterElement = wrapper.firstElementChild as HTMLElement;

    if (!chapterElement) return false;

    // Make verse numbers draggable and add bookmark indicators
    chapterElement.querySelectorAll('.verse-num').forEach((el) => {
      (el as HTMLElement).draggable = true;
    });

    // Add bookmark indicators to this chapter
    state.addBookmarkIndicators(chapterElement, location);

    // Apply study passage outline if active
    state.applyStudyPassageToChapter(chapterElement);

    // Final check before DOM insertion
    if (state.initVersion !== capturedVersion || state.loadedChapters.has(chapterId)) {
      return false;
    }

    if (position === 'before') {
      state.container.insertBefore(chapterElement, state.container.firstChild);
    } else {
      state.container.appendChild(chapterElement);
    }

    state.loadedChapters.set(chapterId, chapterElement);
    state.onChapterLoaded(chapterId, chapterElement);

    // Add end-of-book separator if this is the last chapter of a book
    if (bookInfo.chapterCount - 1 === location.chapterIndex) {
      insertEndOfBookSeparator(chapterElement, bookInfo.name);
    }

    return true;
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      // Expected when initialization is interrupted
      return false;
    }
    console.error(`Error loading chapter ${chapterId}:`, error);
    return false;
  } finally {
    state.loadingChapters.delete(chapterId);
  }
}

/** Insert a visual separator after the last chapter of a book */
function insertEndOfBookSeparator(chapterElement: HTMLElement, bookName: string) {
  const separator = document.createElement('div');
  separator.className = 'end-of-book-separator';
  separator.innerHTML = `
    <div class="end-of-book-line"></div>
    <span class="end-of-book-text">End of the Book of ${bookName}</span>
    <div class="end-of-book-line"></div>
  `;
  chapterElement.insertAdjacentElement('afterend', separator);
}

/** Unload chapters that are too far from the current chapter */
export function unloadDistantChapters(
  container: HTMLElement,
  loadedChapters: Map<string, HTMLElement>,
  visibleChapters: Map<string, number>,
  currentChapterId: string,
  keepChapters: number,
  unobserveChapter: (element: HTMLElement) => void,
): void {
  const currentLocation = parseChapterId(currentChapterId);
  if (!currentLocation) return;

  const currentGlobalIndex = getGlobalChapterIndex(currentLocation);

  const toUnload: Array<{ id: string; element: HTMLElement; isAbove: boolean }> = [];

  for (const [id, element] of loadedChapters) {
    const location = parseChapterId(id);
    if (!location) continue;

    const globalIndex = getGlobalChapterIndex(location);
    if (Math.abs(globalIndex - currentGlobalIndex) > keepChapters) {
      const containerRect = container.getBoundingClientRect();
      const elementRect = element.getBoundingClientRect();
      const isAbove = elementRect.bottom < containerRect.top;
      toUnload.push({ id, element, isAbove });
    }
  }

  // Pin a surviving chapter's on-screen position across the removal
  const unloading = new Set(toUnload.map((u) => u.element));
  const anchor = [...loadedChapters.values()].find((el) => !unloading.has(el));
  const anchorTopBefore = anchor?.getBoundingClientRect().top ?? 0;
  let removedAbove = false;
  for (const { id, element, isAbove } of toUnload) {
    unobserveChapter(element);
    visibleChapters.delete(id);
    // An end-of-book separator follows the book's last chapter — take it too
    const next = element.nextElementSibling;
    if (next?.classList.contains('end-of-book-separator')) next.remove();
    element.remove();
    loadedChapters.delete(id);
    removedAbove ||= isAbove;
  }
  // Callers only unload once scrolling has idled, so pulling scrollTop back by
  // the removed height can't fight momentum — and without it the text jumps
  // by a whole chapter (overflow-anchor is off on the container)
  if (removedAbove && anchor) {
    const shift = anchorTopBefore - anchor.getBoundingClientRect().top;
    container.scrollTo({ top: container.scrollTop - shift, behavior: 'instant' });
  }
}

/** Get a global chapter index for distance calculations */
export function getGlobalChapterIndex(location: BibleLocation): number {
  const OT_CHAPTERS = [
    50, 40, 27, 36, 34, 24, 21, 4, 31, 24, 22, 25, 29, 36, 10, 13, 10, 42, 150, 31, 12, 8, 66, 52,
    5, 48, 12, 14, 3, 9, 1, 4, 7, 3, 3, 3, 2, 14, 4,
  ];
  const NT_CHAPTERS = [
    28, 16, 24, 21, 28, 16, 16, 13, 6, 6, 4, 4, 5, 3, 6, 4, 3, 1, 13, 5, 5, 3, 5, 1, 1, 1, 22,
  ];

  let index = 0;

  if (location.testamentIndex === 1) {
    index = 929;
  }

  const chapters = location.testamentIndex === 0 ? OT_CHAPTERS : NT_CHAPTERS;
  for (let i = 0; i < location.bookIndex && i < chapters.length; i++) {
    index += chapters[i];
  }

  return index + location.chapterIndex;
}
